import { PcbNodeTypes } from "@buildwithflux/models";
import RBush from "rbush";
import { Clipper } from "../../clipper/clipper";
export async function runConnectivityDetection(inputs) {
    const { primitives, shapesPerNode, containerUid, allLayoutHoles, nodeInfos } = inputs;
    // Deserialize
    const primitivesPerNodeClipper = Object.fromEntries(Object.entries(shapesPerNode).map(([nodeUid, data]) => {
        return [nodeUid, data.map((d) => ({ ...d, shape: Clipper.fromObject(d.shapeObject) }))];
    }));
    const allLayoutHolesClipper = allLayoutHoles.map((hole) => ({
        ...hole,
        shape: Clipper.fromObject(hole.shapeObject),
    }));
    // separated by the char $
    const foundEdges = new Set();
    const graphNodes = [];
    //
    // Check the layout holes, subtract them from shapes, and add to graph
    //
    const layoutHolesTree = new RBush();
    layoutHolesTree.load(allLayoutHolesClipper);
    const finalClipperShapesPerNode = {};
    for (const quadTreeNode of primitives) {
        // We are doing one container at a time
        if (quadTreeNode.containerId !== containerUid) {
            continue;
        }
        const primitivesForNode = primitivesPerNodeClipper[quadTreeNode.nodeUid];
        if (!primitivesForNode) {
            continue;
        }
        const nodeInfo = nodeInfos[quadTreeNode.nodeUid];
        if (!nodeInfo) {
            continue;
        }
        for (let i = 0; i < primitivesForNode.length; i++) {
            const primitive = primitivesForNode[i];
            if (!primitive) {
                continue;
            }
            // NOTE: we assume holes cross all layers
            const holesForShape = layoutHolesTree.search(primitive);
            if (holesForShape.length > 0) {
                const holeShapes = [];
                for (const hole of holesForShape) {
                    holeShapes.push(hole.shape);
                }
                primitive.shape = primitive.shape.subtract(holeShapes);
            }
            // The `.shape` is obtained from the underlying primitive. In some cases, an invalid shape
            // is produced with a reversed orientation. An incorrect orrientation means that `.separateShapes()`
            // interprets the primitive as a hole and consequently returns an empty array.
            // This patch tries to detect and resolve incorrectly oriented shapes. It does this by checking for
            // an empty "invalid" shape separation result and "fixing the orientation" if an empty array is detected
            let separatedShapes = primitive.shape.separateShapes();
            if (separatedShapes.length === 0) {
                primitive.shape.fixOrientation();
                separatedShapes = primitive.shape.separateShapes();
            }
            for (let j = 0; j < separatedShapes.length; j++) {
                const shapePart = separatedShapes[j];
                if (!shapePart) {
                    continue;
                }
                const uidForShape = `${quadTreeNode.nodeUid}-conngraph${i}-${j}-layer${primitive.layerId}`;
                const bounds = shapePart.normalizedBoundsRBush();
                const centerPos = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
                const nodeToAdd = {
                    uid: uidForShape,
                    nodeUid: quadTreeNode.nodeUid,
                    netUid: nodeInfo.netUid,
                    connectedLayers: nodeInfo.connectedLayers,
                    layerId: primitive.layerId,
                    noRouting: nodeInfo.noRouting,
                    centerPos,
                    nodeType: nodeInfo.nodeType,
                    ...bounds,
                };
                graphNodes.push(nodeToAdd);
                finalClipperShapesPerNode[uidForShape] = shapePart;
            }
        }
    }
    //
    // STEP 5
    // Prepare an r-tree
    //
    const tree = new RBush();
    tree.load(graphNodes);
    //
    // STEP 6
    // Iterate over all nodes and check for possible copper connections
    //
    const alreadyChecked = new Set();
    for (const node1 of graphNodes) {
        const nearbyNodes = tree.search(node1);
        for (const node2 of nearbyNodes) {
            // same node
            if (node1.uid === node2.uid) {
                continue;
            }
            // We only check pad/trace/via -> fill, not the other way around
            // Reason: the area of a fill is usually larger than the area of a pad/via/trace,
            // so that would produce a lot of false positives in the r-tree search.
            // Checking pad/trace/via -> fill should be equivalent
            if (node1.nodeType === PcbNodeTypes.fill && node2.nodeType !== PcbNodeTypes.fill) {
                continue;
            }
            // different nets
            if (node1.netUid && node2.netUid && node1.netUid !== node2.netUid)
                continue;
            // graph edge already exists (otherwise graphology will throw an error)
            const edgeKey = node1.uid + "$" + node2.uid;
            if (foundEdges.has(edgeKey))
                continue;
            // already checked
            const keyA = node1.uid + "-" + node2.uid;
            const keyB = node2.uid + "-" + node1.uid;
            if (alreadyChecked.has(keyA) || alreadyChecked.has(keyB))
                continue;
            if (
            // different layer
            node1.layerId !== node2.layerId &&
                // special case: we are ok with connecting nodes on different layers if they are the same node (pads, vias)
                // NOTE: this is what makes through hole connections work (vias/pads)
                // TODO: we want to check the copper hole as well for through hole
                !(node1.nodeUid === node2.nodeUid &&
                    (node1.nodeType === PcbNodeTypes.pad || node1.nodeType === PcbNodeTypes.via))) {
                continue;
            }
            // general case: polygon intersection with clipper
            const shape1 = finalClipperShapesPerNode[node1.uid];
            const shape2 = finalClipperShapesPerNode[node2.uid];
            if (!shape1 || !shape2) {
                continue;
            }
            if (Math.abs(shape1.intersect(shape2).totalArea()) > 0) {
                foundEdges.add(edgeKey);
                alreadyChecked.add(keyA);
                alreadyChecked.add(keyB);
            }
        }
    }
    return { foundEdges, graphNodes };
}
//# sourceMappingURL=runConnectivityDetection.js.map